/**
 * And Then Some: the shared wishlist store.
 *
 * Browser memory, no login. Spec: global furniture companion section 8.
 *
 * Exposed as `window.WISH` with: all, has, toggle, remove, count, keyOf.
 * Every read and write is wrapped so a sandboxed preview, a private window,
 * a full quota or corrupted JSON degrades to an in-memory store rather than
 * throwing and taking the page down with it.
 *
 * Entries key on `product|colour|size`, so one product can be saved several
 * ways without colliding: item only, item plus colour, item plus size, or
 * both. In the build `product` is a real variant ID and rows resolve to live
 * product data at render time, rather than storing titles and prices that go
 * stale.
 *
 * INHERITED BUG, do not reintroduce (CLAUDE.md): `[].slice.call(aSet)` returns
 * an EMPTY array. A Set is iterable but not array-like, so the slice trick
 * silently produces nothing. On a sibling build this made the wishlist serialise
 * empty while count() and has() kept reporting the right numbers from the
 * in-memory Set, so it looked healthy until a reload. Always Array.from().
 */
(function () {
  'use strict';

  var KEY = 'ats_wishlist';
  var EVENT = 'wishlist:change';

  /** In-memory fallback. Also the working copy: localStorage is a mirror. */
  var entries = new Set();

  /** Set false the first time storage throws, so we stop retrying every write. */
  var storageAvailable = true;

  function readStorage() {
    if (!storageAvailable) return null;
    try {
      return window.localStorage.getItem(KEY);
    } catch (error) {
      storageAvailable = false;
      return null;
    }
  }

  function writeStorage(value) {
    if (!storageAvailable) return;
    try {
      window.localStorage.setItem(KEY, value);
    } catch (error) {
      // Quota, private mode, or a blocked sandbox. Keep the in-memory Set as
      // the source of truth for this page view and stop touching storage.
      storageAvailable = false;
    }
  }

  /**
   * Load once at startup. Anything that is not an array of strings is treated
   * as corrupt and discarded, rather than allowed to poison later reads.
   */
  function load() {
    var raw = readStorage();
    if (!raw) return;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      // Corrupted JSON. Start clean rather than break the page.
      return;
    }

    if (!Array.isArray(parsed)) return;

    for (var i = 0; i < parsed.length; i++) {
      if (typeof parsed[i] === 'string' && parsed[i]) entries.add(parsed[i]);
    }
  }

  function save() {
    // Array.from, NOT [].slice.call. See the note at the top of this file.
    writeStorage(JSON.stringify(Array.from(entries)));
  }

  function announce() {
    document.dispatchEvent(
      new CustomEvent(EVENT, { detail: { count: entries.size }, bubbles: true })
    );
  }

  /**
   * Build the composite key. Blank colour or size are preserved as empty
   * segments so `123||M` and `123|Black|` stay distinct from each other and
   * from the bare `123||`.
   *
   * @param {string|number} product - Variant ID in the build, handle in the demos.
   * @param {string} [colour]
   * @param {string} [size]
   * @returns {string}
   */
  function keyOf(product, colour, size) {
    return [product == null ? '' : String(product), colour || '', size || ''].join('|');
  }

  var WISH = {
    keyOf: keyOf,

    /** @returns {string[]} every saved key, newest last. */
    all: function () {
      return Array.from(entries);
    },

    /** @returns {number} */
    count: function () {
      return entries.size;
    },

    /** @returns {boolean} */
    has: function (product, colour, size) {
      return entries.has(keyOf(product, colour, size));
    },

    /**
     * Add if absent, remove if present.
     * @returns {boolean} true if the entry is now saved.
     */
    toggle: function (product, colour, size) {
      var key = keyOf(product, colour, size);
      var saved;

      if (entries.has(key)) {
        entries.delete(key);
        saved = false;
      } else {
        entries.add(key);
        saved = true;
      }

      save();
      announce();
      return saved;
    },

    /** @returns {boolean} true if something was actually removed. */
    remove: function (product, colour, size) {
      var removed = entries.delete(keyOf(product, colour, size));
      if (removed) {
        save();
        announce();
      }
      return removed;
    },

    /** The event name every consumer listens on, so nobody hardcodes it. */
    EVENT: EVENT,
  };

  load();
  window.WISH = WISH;

  // Keep multiple tabs consistent. Cheap, and avoids a stale badge after the
  // shopper saves something in another tab.
  window.addEventListener('storage', function (event) {
    if (event.key !== KEY) return;
    entries = new Set();
    load();
    announce();
  });

  // Let anything that loaded before this module pick up the initial count.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', announce);
  } else {
    announce();
  }
})();
